/**
 * Filtros da tela de Pipelines. O estado mora na URL com os MESMOS nomes de
 * parâmetro da API (`GET /businesses`), então a querystring da tela é também o
 * `filter` das ações em lote — "mover todos os filtrados" pega exatamente o
 * que está na tela.
 */

export type BusinessView = 'kanban' | 'lista' | 'painel';

/** Parâmetros da URL que não são filtro. */
const PAGE_KEYS = new Set(['p', 'view', 'stage']);

export const BUSINESS_SORT_LABELS = {
  recent: 'Mais recentes',
  oldest: 'Mais antigos',
  moved_recent: 'Movidos recentemente',
  moved_oldest: 'Parados há mais tempo',
} as const;
export type BusinessSort = keyof typeof BUSINESS_SORT_LABELS;

export const STATUS_LABELS = {
  in_process: 'Em andamento',
  won: 'Ganhos',
  lost: 'Perdidos',
  all: 'Todos',
} as const;
export type StatusFilter = keyof typeof STATUS_LABELS;

/** Os filtros do painel, na ordem em que aparecem. */
export const FILTER_FIELDS = [
  { key: 'status', label: 'Status' },
  { key: 'attendant', label: 'Atendente' },
  { key: 'tags', label: 'Tags do lead' },
  { key: 'products', label: 'Produtos' },
  { key: 'minTotal', label: 'Valor mínimo' },
  { key: 'maxTotal', label: 'Valor máximo' },
  { key: 'lossReasonId', label: 'Motivo de perda' },
  { key: 'source', label: 'Origem do lead' },
  { key: 'lastMovedAtFrom', label: 'Movido desde' },
  { key: 'lastMovedAtTo', label: 'Movido até' },
  { key: 'statusChangedAtFrom', label: 'Ganho/perdido desde' },
  { key: 'statusChangedAtTo', label: 'Ganho/perdido até' },
  { key: 'additionalField', label: 'Campo do negócio' },
  { key: 'leadAdditionalField', label: 'Campo do lead' },
] as const;
export type FilterKey = (typeof FILTER_FIELDS)[number]['key'];

/** Intervalo de criação — controle próprio no cabeçalho, vazio por padrão (Q21). */
export const INTERVAL_KEYS = ['createdAtFrom', 'createdAtTo'] as const;

/** Só os parâmetros de filtro (inclui busca, ordenação e intervalo). */
export function filterParamsOf(params: URLSearchParams): URLSearchParams {
  const result = new URLSearchParams();
  params.forEach((value, key) => {
    if (!PAGE_KEYS.has(key) && value !== '') result.set(key, value);
  });
  return result;
}

/** Filtro do Painel: sem status padrão — conta em andamento, ganhos e perdidos. */
export function panelFilter(params: URLSearchParams): string {
  const filters = filterParamsOf(params);
  filters.delete('sort');
  filters.sort();
  return filters.toString();
}

/**
 * Querystring enviada à API. Sem status escolhido, a tela mostra todos os
 * negócios da etapa, como o DataCrazy — ganhos e perdidos com a fita no card.
 */
export function apiFilter(params: URLSearchParams): string {
  const filters = filterParamsOf(params);
  if (!filters.has('status')) filters.set('status', 'all');
  filters.sort();
  return filters.toString();
}

/** Quantos filtros do painel estão ativos (para o contador do botão). */
export function activeFilterCount(params: URLSearchParams): number {
  return FILTER_FIELDS.filter(({ key }) => params.get(key)).length;
}

/** "some id1,id2" → { operator, ids } (mesmo formato da API). */
export function parseIdList(raw: string | null): { operator: string; ids: string[] } {
  if (!raw) return { operator: 'some', ids: [] };
  const [operator, list = ''] = raw.split(' ');
  return { operator, ids: list.split(',').filter(Boolean) };
}

export const toIdList = (operator: string, ids: string[]): string =>
  ids.length ? `${operator} ${ids.join(',')}` : '';

/** Data do input (yyyy-mm-dd) → ISO no começo ou no fim do dia. */
export function dayBoundary(day: string, end: boolean): string {
  if (!day) return '';
  return new Date(`${day}T${end ? '23:59:59.999' : '00:00:00'}`).toISOString();
}

export const isoToDay = (iso: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
